import {
  BackendApiChangeStatusRequest,
  BackendFoodTypeStatusChangePayload,
  BackendOrderStatusChangePayload,
  CheckoutData,
  CreatePhoneInOrderPayload,
  Order,
  PaymentDetails,
  PhoneInOrderResponse,
  PrintJob,
} from "@lemmonhq/types";
import { ShopContextType } from "@app/vue/providers/ShopConnection";
import { instrumentation } from "@lemmonhq/analytics";
import { getAPIClient, routingFetch } from "../helpers";

export const ordersAPI = {
  acceptOrder: async (orderId: number) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.kds.accept.$post({
      json: { orderId },
    });
    if (resp.ok) {
      const json = await resp.json();
      return json;
    } else {
      throw new Error();
    }
  },
  rejectOrder: async (orderId: number) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.kds.reject.$post({
      json: { orderId },
    });
    if (resp.ok) {
      const json = await resp.json();
      return json;
    } else {
      throw new Error();
    }
  },
  //
  getOrders: async (orderIds?: number[]): Promise<Order[]> => {
    if (!orderIds || orderIds.length === 0) {
      return [];
    }
    const hc = getAPIClient();
    const response = orderIds
      ? await hc.api.orders.$get({ query: { orderIds: orderIds.join(",") } })
      : await hc.api.orders.$get();

    if (!response.ok) {
      throw new Error("Failed to fetch getOrders");
    }
    return response.json();
  },

  getDailyOrders: async (): Promise<Order[]> => {
    const hc = getAPIClient();
    const response = await hc.api.orders.daily.$get();
    if (!response.ok) {
      throw new Error("Failed to fetch getDailyOrders");
    }
    return response.json();
  },

  getActiveOrders: async (): Promise<Order[]> => {
    const hc = getAPIClient();
    const response = await hc.api.orders.active.$get();
    if (!response.ok) {
      throw new Error("Failed to fetch getActiveOrders");
    }
    return response.json();
  },
  createOrder: async (
    payload: CheckoutData,
    shop: ShopContextType,
  ): Promise<{
    order: Order;
    printJobs: PrintJob[];
    payment?: PaymentDetails;
  }> => {
    const hc = getAPIClient();
    const response = await hc.api.orders.$post({
      json: payload as any,
    });
    if (!response.ok) {
      const error: any = await response.json();
      throw new Error(error.message || "Failed to createOrder");
    }
    const resp: {
      order: Order;
      printJobs: PrintJob[];
      payment?: PaymentDetails;
    } = await response.json();
    const { order, payment, printJobs } = resp;
    const eventAttrs = {
      order_id: order.id,
      order_service_method: order.service_method,
      order_table_id: order.table_id,
      order_payment_id: payment?.id,
      order_payment_method: payment?.method,
      order_payment_amount: payment?.amount,
      order_print_jobs_length: printJobs?.length,
    };
    instrumentation.trackRevenue(resp.order.id, resp.order.amount, eventAttrs);
    instrumentation.track("order_created", eventAttrs);
    try {
      await ordersAPI.processOrderCreation(shop, resp);
    } catch {}
    return resp;
  },
  createPhoneInOrder: async (
    shop: ShopContextType,
    payload: CreatePhoneInOrderPayload,
  ): Promise<PhoneInOrderResponse> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.orders["phone-in"].$post({
      json: payload,
    });
    if (resp.ok) {
      const json = (await resp.json()) as PhoneInOrderResponse;
      try {
        await ordersAPI.processOrderCreation(shop, json);
      } catch {}
      return json;
    }

    let message = "Failed to create phone-in order";
    try {
      const errorBody: any = await resp.json();
      if (errorBody?.message) {
        message = errorBody.message;
      }
    } catch {
      // ignore json parse errors
    }
    throw new Error(message);
  },

  async processOrderCreation(
    shop: ShopContextType,
    resp: { order: Order; printJobs: PrintJob[] },
  ) {
    console.log(resp);
    Promise.all(
      resp.printJobs
        .filter((el) => el.doc.type === "kitchenTicket")
        .map(async (job) => shop.print(job)),
    ).catch((e) => {
      throw e;
      // do nothing since shop dispatches a toast
    });
  },

  changeOrderStatus: async (
    payload: BackendOrderStatusChangePayload,
  ): Promise<{ orders: Order[]; kitchenTickets?: PrintJob[] }> => {
    const hc = getAPIClient();
    const response = await hc.api.orders[":orderId"].$put({
      param: { orderId: payload.orderID.toString() },
      json: payload as any,
    });
    if (!response.ok) {
      throw new Error("Failed to changeOrderStatus");
    }
    const resp = await response.json();
    return resp as { orders: Order[]; kitchenTickets?: PrintJob[] };
  },
  changeFoodTypeStatus: async (
    payload: BackendFoodTypeStatusChangePayload,
  ): Promise<{ orders: Order[]; kitchenTickets?: PrintJob[] }> => {
    const hc = getAPIClient();
    const response = await hc.api.orders[":orderId"].$put({
      param: { orderId: payload.orderID.toString() },
      json: payload as any,
    });
    if (!response.ok) {
      throw new Error("Failed to changeOrderStatus");
    }
    const resp = await response.json();
    return resp as { orders: Order[]; kitchenTickets?: PrintJob[] };
  },
  closeOpenOrder: async (order: Order): Promise<{ orders: Order[] }> => {
    const payload: BackendApiChangeStatusRequest = {
      type: "order",
      orderID: order.id,
      orderStatus: "closed",
      currentStatus: order.status,
      foodTypeId: undefined,
    };
    const hc = getAPIClient();
    const response = await hc.api.orders[":orderId"].$put({
      param: { orderId: payload.orderID.toString() },
      json: payload as any,
    });
    if (!response.ok) {
      throw new Error("Failed to closeOpenOrder");
    }
    const resp = await response.json();
    return resp as { orders: Order[] };
  },
  updateOrderItemQuantity: async function (
    orderId: number,
    payload: Record<string, number>,
  ) {
    const response = await routingFetch(`/api/orders/${orderId}/orderItems/quantity`, {
      method: "put",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to updateOrderItemQuantity");
    }
  },
};
